import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import CachedIcon from '@material-ui/icons/Cached';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useRef } from 'react';
import { getIconStatus } from 'src/app/utils/vehicleService';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { getVehicleSystemStatus, exportExcelTrackingStatus } from 'src/features/vehicleSlice';
import { useQuery } from 'src/app/hooks/useQuery';
import { getTransmissionStatusText } from 'src/app/utils/commomService';


const COLUMNS = [
  { id: 'index', label: 'STT', width: '4%', align: 'center' },
  { id: 'license_plate', label: 'Biển số', width: '13%' },
  { id: 'device_type', label: 'Mã thiết bị', width: '12%' },
  { id: 'speed', label: 'V (km/h)', width: '4%', align: 'center' },
  { id: 'time', label: 'Thời gian', width: '8%', align: 'center' },
  { id: 'dailyKm', label: 'Km trong ngày', width: '7%', align: 'center' },
  { id: 'location', label: 'Khu vực', width: '24%' },
  { id: 'driverName', label: 'Lái xe', width: '8%', align: 'center' },
  { id: 'driverLicense', label: 'Bằng lái', width: '8%', align: 'center' },
  { id: 'airConditioner', label: 'Điều hòa', width: '5%', align: 'center' },
  { id: 'temperature', label: 'Nhiệt độ', width: '5%', align: 'center' },
  {
    id: 'continuousDrivingTime',
    label: 'TG LX liên tục',
    width: '7%',
    align: 'center'
  },
  {
    id: 'dailyDrivingTime',
    label: 'TG LX trong ngày',
    width: '7%',
    align: 'center'
  },
  {
    id: 'status_transmission',
    label: 'Tích truyền',
    width: '6%',
    align: 'center'
  }
];

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    position: 'fixed',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    margin: 0,
    width: 'min(900px, calc(100vw - 32px))',
    maxWidth: '900px',
    overflow: 'hidden',
    borderRadius: 8
  },
  root: {
    backgroundColor: '#fff'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 2),
    borderBottom: '1px solid #d7d7d7'
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  titleIcon: {
    color: '#d9042f',
    fontSize: 28
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#4b4b4b',
    lineHeight: 1
  },
  filterBar: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: theme.spacing(0),
    alignItems: 'center',
    padding: theme.spacing(1.25, 2)
  },
  filterLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: '#555'
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    height: 38,
    padding: theme.spacing(0, 0),
    border: '1px solid #d2d6dc',
    borderRadius: 4,
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#444',
    '& input::placeholder': {
      color: '#aab2bb',
      opacity: 1
    }
  },
  autocompleteRoot: {
    flex: 1
  },
  autocompleteInputRoot: {
    padding: '0 12px',
    minHeight: '38px',
    '& .MuiAutocomplete-input:first-child': {
      padding: '6px 0'
    }
  },
  clearIndicator: {
    paddingRight: '15px'
  },
  closeButton: {
    color: '#d9042f',
  },
  tableContainer: {
    maxHeight: '420px',
    overflowX: 'hidden',
    padding: '0 16px 8px'
  },
  table: {
    width: '100%',
    tableLayout: 'fixed'
  },
  headCell: {
    backgroundColor: '#d8dde3',
    color: '#4d4d4d',
    fontSize: 11,
    fontWeight: 700,
    border: '1px solid #c3cad2',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.25,
    padding: '6px 4px',
    textAlign: 'center'
  },
  bodyCell: {
    fontSize: 11,
    color: '#525252',
    border: '1px solid #d7dce2',
    verticalAlign: 'middle',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.25,
    padding: '6px 4px'
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f9ff'
    }
  },
  matchedRow: {
    backgroundColor: '#d9042f',
    '& td': {
      backgroundColor: '#d9042f',
      color: '#ffffff'
    },
    '&:hover': {
      backgroundColor: '#b80327 !important'
    },
    '&:hover td': {
      backgroundColor: '#b80327 !important',
      color: '#ffffff'
    },
    '& span': {
      color: '#ffffff'
    }
  },
  plateCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0
    }
  },
  plateIcon: {
    color: '#2d89ef',
    fontSize: 14,
    flexShrink: 0
  },
  emptyBox: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#77808a'
  },
  '@media (max-width: 960px)': {
    dialogPaper: {
      right: theme.spacing(1),
      bottom: theme.spacing(1),
      width: 'calc(100vw - 16px)',
      maxWidth: 'calc(100vw - 16px)'
    },
    tableContainer: {
      maxHeight: '50vh'
    }
  }
}));

const DirectionsVehicleIcon = ({ vehicle }) => {
  const vehicleStatus = vehicle?.device_status;
  const vehicleType = vehicle?.vehicle_type;
  const iconSrc =
    vehicle?.debt === true || vehicle?.rawData?.debt === true
      ? '/static/iconSvg/motors/motor_expired.svg'
      : getIconStatus(vehicleStatus, vehicleType);

  return (
    <Box style={{ width: '27px', height: '27px' }}>
      <img alt="car" src={iconSrc} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

const SystemStatusTable = ({
  open = false,
  onClose = () => { },
  onRefresh = () => { },
  rows = [],
  onRowClick,
  onLoadMore,
  isLoading = false,
  hasMore = false,
  title = 'Danh sách phương tiện'
}) => {
  const classes = useStyles();
  const dispatch = useDispatch()
  const [keyword, setKeyword] = useState('');

  const queryUrl = useQuery();

  const filterQuery = useMemo(() => ({
    agency_id: queryUrl.get('agency_id') || undefined,
    end_user_id: queryUrl.get('end_user_id') || undefined
  }), [queryUrl]);

  const initStateFromValue = {
    device_id: '',
    license_plate: ''
  };

  const [query, setQuery] = useState(initStateFromValue);

  const scrollRef = useRef(null);
  const listLicensePlate = useSelector(
    state => state.deviceSlice.listLicensePlate
  );
  const listVehicleSystemStatus = useSelector(
    state => state.vehicleSlice.listVehicleSystemStatus
  );

  useEffect(() => {
    if (keyword.trim() && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [keyword]);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setQuery(initStateFromValue);
    }
  }, [open]);

  const licensePlateOptions = listLicensePlate.filter(
    plate => plate.license_plate !== null
  );

  const handleChangeDevice = (event, value) => {
    if (!value || typeof value === 'string') {
      setKeyword('');
      setQuery(initStateFromValue);
      onRefresh();
      return;
    }

    dispatch(getVehicleSystemStatus({
      id: filterQuery.end_user_id,
      data: {
        agency_id: filterQuery.agency_id,
        keywords: value.license_plate.trim() ? value.license_plate.trim() : undefined,
      }
    }));

    setQuery({
      device_id: Number(value.device_id) || '',
      license_plate: value.license_plate || ''
    });
    setKeyword(value.license_plate || '');
  };

  const filteredRows = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();

    if (!normalizedKeyword) {
      return rows;
    }

    // Merge: kết quả search từ Redux + existing rows
    const existingDeviceIds = new Set(rows.map(r => r.device_id));

    // Thêm kết quả search từ Redux (chỉ những hàng chưa có trong rows)
    const searchResults = (listVehicleSystemStatus || []).filter(
      item => !existingDeviceIds.has(item.device_id)
    );

    const allRows = [...searchResults, ...rows];

    // Tìm các hàng match với keyword
    const matchedRows = [];
    const unmatchedRows = [];

    allRows.forEach((row, index) => {
      const licensePlate = String(
        row?.license_plate || row?.licensePlate || ''
      ).toLowerCase();
      const isExactMatch = licensePlate === normalizedKeyword;
      const isStartMatch = licensePlate.startsWith(normalizedKeyword);
      const isIncludedMatch = licensePlate.includes(normalizedKeyword);

      const rowWithPriority = {
        row,
        originalIndex: index,
        isMatched: isIncludedMatch,
        priority: isExactMatch ? 0 : isStartMatch ? 1 : isIncludedMatch ? 2 : 3
      };

      if (isIncludedMatch) {
        matchedRows.push(rowWithPriority);
      } else {
        unmatchedRows.push(rowWithPriority);
      }
    });

    // Sort matched rows theo priority
    matchedRows.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return a.originalIndex - b.originalIndex;
    });

    // Sort unmatched rows theo index gốc
    unmatchedRows.sort((a, b) => a.originalIndex - b.originalIndex);

    // Merge: matched rows lên đầu, sau đó unmatched rows
    return [...matchedRows, ...unmatchedRows].map(item => item.row);
  }, [keyword, rows, listVehicleSystemStatus]);

  const handleScroll = event => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (
      isNearBottom &&
      !isLoading &&
      hasMore &&
      onLoadMore
    ) {
      onLoadMore();
    }
  };

  const handleRefresh = () => {
    setKeyword('');
    setQuery(initStateFromValue);
    onRefresh();
  };

  const handleExportExcel = () => {
    const agencyId = filterQuery.agency_id;
    if (!agencyId) {
      return;
    }

    dispatch(exportExcelTrackingStatus({
      id: filterQuery.end_user_id,
      data: {
        agency_id: filterQuery.agency_id,
      }
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <DirectionsCarIcon className={classes.titleIcon} />
            <Typography className={classes.title}>{title}</Typography>
          </Box>
          <Box>
            <Tooltip title="Xuất Excel">
              <IconButton
                onClick={handleExportExcel}
                className={classes.closeButton}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Làm mới">
              <IconButton
                onClick={handleRefresh}
                className={classes.closeButton}
              >
                <CachedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Đóng">
              <IconButton onClick={onClose} className={classes.closeButton}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box className={classes.filterBar}>
          <Typography className={classes.filterLabel}></Typography>
          <Paper elevation={0} className={classes.searchBox}>
            <Autocomplete
              freeSolo
              options={licensePlateOptions}
              getOptionLabel={option =>
                typeof option === 'string' ? option : option.license_plate || ''
              }
              value={
                query.device_id
                  ? licensePlateOptions.find(
                    item => item.device_id === Number(query.device_id)
                  )
                  : null
              }
              onChange={handleChangeDevice}
              className={classes.autocompleteRoot}
              classes={{
                inputRoot: classes.autocompleteInputRoot,
                clearIndicator: classes.clearIndicator
              }}
              noOptionsText="Không có biển số phù hợp"
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Nhập biển số để tìm kiếm"
                  className={classes.searchInput}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    startAdornment: (
                      <SearchIcon
                        style={{ color: '#9aa4af', marginRight: 8 }}
                      />
                    )
                  }}
                />
              )}
            />
          </Paper>
        </Box>

        <TableContainer
          ref={scrollRef}
          className={classes.tableContainer}
          onScroll={handleScroll}
        >
          <Table stickyHeader className={classes.table}>
            <TableHead>
              <TableRow>
                {COLUMNS.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    className={classes.headCell}
                    style={{ width: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length > 0 ? (
                filteredRows.map((row, index) =>
                  (() => {
                    const normalizedKeyword = keyword.trim().toLowerCase();
                    const isMatched =
                      normalizedKeyword &&
                      String(row?.license_plate || row?.licensePlate || '')
                        .toLowerCase()
                        .includes(normalizedKeyword);

                    return (
                      <TableRow
                        hover
                        key={row.id || `${row.license_plate}-${index}`}
                        onClick={() => onRowClick && onRowClick(row, index)}
                        className={[
                          onRowClick ? classes.clickableRow : '',
                          isMatched ? classes.matchedRow : ''
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <TableCell align="center" className={classes.bodyCell}>
                          {index + 1}
                        </TableCell>
                        <TableCell className={classes.bodyCell}>
                          <Tooltip title={row.license_plate || '-'}>
                            <Box className={classes.plateCell}>
                              <Box style={{ flexShrink: 0 }}>
                                <DirectionsVehicleIcon vehicle={row} />
                              </Box>
                              <span>{row.license_plate || '-'}</span>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.device_type ? row.device_type : '--'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.speed_gps ?? 0}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.created_at
                            ? moment(row.created_at * 1000).format(
                              'HH:mm:ss DD-MM-yyyy'
                            )
                            : '--'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.daily_distance ?? 0}
                        </TableCell>
                        <TableCell className={classes.bodyCell}>
                          {row.location || '-'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.driver_name || '-'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.driver_license || '-'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.airConditioner || '-'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.temperature || '-'}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.non_stop_driving_time ?? 0}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row.total_drive_time ?? 0}
                        </TableCell>
                        <TableCell align="center" className={classes.bodyCell}>
                          {row?.deviceType === 'VGPS_CAR4G'
                            ? getTransmissionStatusText(row?.status_transmission)
                            : '--'}
                        </TableCell>
                      </TableRow>
                    );
                  })()
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className={classes.bodyCell}
                  >
                    <Box className={classes.emptyBox}>
                      Không có dữ liệu phù hợp
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {isLoading && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} color="secondary" />
            </Box>
          )}
        </TableContainer>
      </Box>
    </Dialog>
  );
};

export default SystemStatusTable;
